import React, { useState, useEffect, useCallback } from "react";
import { DualHeader } from "@/components/DualHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, RotateCcw, ShoppingCart, Sailboat } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { FishingRegulations } from "@/components/FishingRegulations";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useLocation } from "react-router-dom";
import { addDays, differenceInDays } from "date-fns";
import { PermitTypeSelection } from "@/components/permits/PermitTypeSelection";
import { FishingDateSelection } from "@/components/permits/FishingDateSelection";
import { CampingInfo } from "@/components/permits/CampingInfo";
import { PermitSummary } from "@/components/permits/PermitSummary";
import { PermitNotes } from "@/components/permits/PermitNotes";
import { CustomerInfoForm } from "@/components/permits/CustomerInfoForm";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useCart } from "@/contexts/CartContext";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, ShieldAlert, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Dog } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { calculateCampingPrice, calculateDays as utilsCalculateDays, calculateNights as utilsCalculateNights, calculateFishingPrice, calculateTotalPrice } from "@/utils/permitUtils";
import { ClosureNotice } from "@/components/ClosureNotice";
import { CLOSURES, CLOSURE_MESSAGE_GENERAL } from "@/config/closures";
const FishingPermits = () => {
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    addItem,
    updateItem
  } = useCart();

  // Extract editItem from location state if it exists
  const editItemData = location.state?.editItem;
  const isEditMode = !!editItemData;
  const [permitType, setPermitType] = useState("daily-all");
  const [days, setDays] = useState(1);
  const [totalPrice, setTotalPrice] = useState(25.00);
  const [fishingStart, setFishingStart] = useState<Date | undefined>(undefined);
  const [fishingEnd, setFishingEnd] = useState<Date | undefined>(undefined);
  const [campingDays, setCampingDays] = useState(1);
  const [campingStartDate, setCampingStartDate] = useState<Date | undefined>(undefined);
  const [campingEndDate, setCampingEndDate] = useState<Date | undefined>(undefined);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bringingPets, setBringingPets] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [legalAcknowledgment, setLegalAcknowledgment] = useState(false);
  const [boatInspectionAcknowledged, setBoatInspectionAcknowledged] = useState(false);
  const [acknowledgeModalOpen, setAcknowledgeModalOpen] = useState(false);
  const [legalTermsModalOpen, setLegalTermsModalOpen] = useState(false);
  const [safetyModalOpen, setSafetyModalOpen] = useState(false);
  const [boatInspectionModalOpen, setBoatInspectionModalOpen] = useState(false);
  const [cascadeAnglersClient, setCascadeAnglersClient] = useState<boolean | null>(null);

  // Corrected useEffect for loading edit item data and calculating camping nights
  useEffect(() => {
    if (isEditMode && editItemData) {
      console.log("[FishingPermits] Loading edit data:", editItemData);

      // Map permitName back to permitType
      let mappedPermitType = "daily-all";
      if (editItemData.permitName === "All Fisheries (Ages 14+)") {
        mappedPermitType = "daily-all";
      } else if (editItemData.permitName === "Two Poles (Ages 14+)" || editItemData.permitName === "Mtn. View/Sheep Creek with 2 poles") {
        mappedPermitType = "daily-two-poles";
      } else if (editItemData.permitName === "Individual Fishing - All Fisheries") {
        mappedPermitType = "annual-individual";
      } else if (editItemData.permitName === "Individual Fishing w/2 poles") {
        mappedPermitType = "annual-individual-two-poles";
      } else if (editItemData.permitName === "Family Fishing - All Fisheries") {
        mappedPermitType = "annual-family";
      } else if (editItemData.permitName === "Family Fishing w/2 poles") {
        mappedPermitType = "annual-family-two-poles";
      }
      setPermitType(mappedPermitType);
      setFishingStart(editItemData.startDate ? new Date(editItemData.startDate) : new Date());
      setFishingEnd(editItemData.endDate ? new Date(editItemData.endDate) : new Date());
      setCustomerName(editItemData.customerName || "");
      setCustomerEmail(editItemData.customerEmail || "");
      setBringingPets(editItemData.bringingPets || false);
      setPhoneNumber(editItemData.phoneNumber || "");
      setAddress(editItemData.address || "");
      setCity(editItemData.city || "");
      setState(editItemData.state || "");
      setZipCode(editItemData.zipCode || "");
      setLicensePlate(editItemData.licensePlate || "");
      setCascadeAnglersClient(editItemData.cascadeAnglersClient ?? null);
      setLegalAcknowledgment(true);

      // Use saved camping dates if they exist, otherwise derive from fishing dates
      if (editItemData.includesCamping) {
        if (editItemData.campingStartDate) {
          setCampingStartDate(new Date(editItemData.campingStartDate));
        } else if (editItemData.startDate) {
          setCampingStartDate(new Date(editItemData.startDate));
        }
        if (editItemData.campingEndDate) {
          setCampingEndDate(new Date(editItemData.campingEndDate));
        } else if (editItemData.endDate) {
          setCampingEndDate(new Date(editItemData.endDate));
        }
        
        // Use campingTotal to derive nights, or calculate from camping dates
        const campStart = editItemData.campingStartDate ? new Date(editItemData.campingStartDate) : new Date(editItemData.startDate);
        const campEnd = editItemData.campingEndDate ? new Date(editItemData.campingEndDate) : new Date(editItemData.endDate);
        const campingNights = utilsCalculateNights(campStart, campEnd);
        setCampingDays(campingNights);

        console.log("[FishingPermits] Loaded camping dates from edit data:", {
          campingStartDate: campStart,
          campingEndDate: campEnd,
          calculatedCampingNights: campingNights
        });
      } else {
        setCampingDays(0);
        console.log("[FishingPermits] No camping in edit data.");
      }
    }
  }, [isEditMode, editItemData]);

  // Helper function to calculate days between dates (inclusive)
  const calculateDaysFromDates = (startDate: Date, endDate: Date): number => {
    return utilsCalculateDays(startDate, endDate);
  };

  // Helper function to calculate days between dates (inclusive)
  const calculateDays = useCallback(() => {
    if (fishingStart && fishingEnd) {
      return utilsCalculateDays(fishingStart, fishingEnd);
    }
    return 1;
  }, [fishingStart, fishingEnd]);
  useEffect(() => {
    const calculatedDays = calculateDays();
    setDays(calculatedDays);
  }, [fishingStart, fishingEnd, calculateDays]);
  useEffect(() => {
    let price = 0;
    switch (permitType) {
      case "daily-all":
        price += 25 * days;
        break;
      case "daily-two-poles":
        price += 40 * days;
        break;
      case "annual-individual":
        price += 150;
        break;
      case "annual-individual-two-poles":
        price += 180;
        break;
      case "annual-family":
        price += 200;
        break;
      case "annual-family-two-poles":
        price += 220;
        break;
    }
    if (campingDays > 0) {
      price += 15 * campingDays;
    }
    setTotalPrice(price);
  }, [permitType, days, campingDays]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerName || !customerEmail) {
      toast({
        title: "Missing information",
        description: "Please provide your name and email to continue.",
        variant: "destructive"
      });
      return;
    }
    if (cascadeAnglersClient === null) {
      toast({
        title: "Missing information",
        description: "Please indicate whether you are a client of Cascade Anglers.",
        variant: "destructive"
      });
      return;
    }
    if (permitType.startsWith("daily") && (!fishingStart || !fishingEnd)) {
      toast({
        title: "Missing dates",
        description: "Please select your fishing dates to continue.",
        variant: "destructive"
      });
      return;
    }
    if (!legalAcknowledgment) {
      setAcknowledgeModalOpen(true);
      return;
    }
    setIsSubmitting(true);
    try {
      let permitName = "";
      let basePrice = 0;
      let isAnnual = false;
      let extraPoles: number | null = null;
      switch (permitType) {
        case "daily-all":
          permitName = "All Fisheries (Ages 14+)";
          basePrice = 25;
          extraPoles = null;
          break;
        case "daily-two-poles":
          permitName = "Mtn. View/Sheep Creek with 2 poles";
          basePrice = 40;
          extraPoles = 2;
          break;
        case "annual-individual":
          permitName = "Individual Fishing - All Fisheries";
          basePrice = 150;
          isAnnual = true;
          break;
        case "annual-individual-two-poles":
          permitName = "Individual Fishing w/2 poles";
          basePrice = 180;
          isAnnual = true;
          extraPoles = 2;
          break;
        case "annual-family":
          permitName = "Family Fishing - All Fisheries";
          basePrice = 200;
          isAnnual = true;
          break;
        case "annual-family-two-poles":
          permitName = "Family Fishing w/2 poles";
          basePrice = 220;
          isAnnual = true;
          extraPoles = 2;
          break;
      }

      // For annual permits, default dates if not selected
      const effectiveStart = fishingStart || (isAnnual ? new Date() : fishingStart);
      const actualEndDate = isAnnual ? new Date(new Date().getFullYear(), 11, 31) : fishingEnd;
      const effectiveEnd = fishingEnd || actualEndDate;
      
      // Calculate fishing price based on permit type
      const calculatedDays = utilsCalculateDays(effectiveStart!, effectiveEnd!);
      const fishingPrice = isAnnual ? basePrice : (basePrice * calculatedDays);
      
      // Use the actual camping dates (which may differ from fishing dates if user overrode them)
      const effectiveCampStart = campingStartDate || effectiveStart;
      const effectiveCampEnd = campingEndDate || effectiveEnd;
      const campingNights = effectiveCampStart && effectiveCampEnd ? utilsCalculateNights(effectiveCampStart, effectiveCampEnd) : 0;
      // If includesCamping is true but nights is 0 (same day), we still charge for 1 night
      const effectiveCampingNights = campingDays > 0 && campingNights === 0 ? 1 : campingNights;
      const campingTotalPrice = campingDays > 0 ? calculateCampingPrice(effectiveCampingNights) : 0;
      
      // Calculate the actual total price
      const actualTotalPrice = fishingPrice + campingTotalPrice;
      
      console.log("[FishingPermits] Price calculation:", {
        permitName,
        basePrice,
        calculatedDays,
        fishingPrice,
        effectiveCampingNights,
        campingTotalPrice,
        actualTotalPrice,
        stateTotalPrice: totalPrice
      });
      if (isEditMode && editItemData) {
        // Update existing item
          updateItem(editItemData.id, {
          type: 'fishing',
          permitCategory: 'fishing',
          permitName,
          isAnnual,
          customerName,
          customerEmail,
          startDate: effectiveStart!,
          endDate: effectiveEnd!,
          includesCamping: campingDays > 0,
          basePrice,
          extraPoles,
          totalPrice: actualTotalPrice,
          campingTotal: campingTotalPrice,
          campingStartDate: campingDays > 0 ? effectiveCampStart : null,
          campingEndDate: campingDays > 0 ? effectiveCampEnd : null,
          bringingPets,
          phoneNumber,
          address,
          city,
          state,
          zipCode,
          licensePlate,
          cascadeAnglersClient,
          legalAcknowledgment,
          boatInspectionAcknowledged,
          days: calculatedDays,
          siteId: editItemData.siteId
        });
        toast({
          title: "Permit updated",
          description: "Your fishing permit has been updated."
        });
      } else {
        // Create new item
        const fishingSiteId = `fishing-${permitType}-${Date.now()}`;
        addItem({
          id: fishingSiteId,
          type: 'fishing',
          permitCategory: 'fishing',
          permitName,
          isAnnual,
          customerName,
          customerEmail,
          startDate: effectiveStart!,
          endDate: effectiveEnd!,
          includesCamping: campingDays > 0,
          basePrice,
          extraPoles,
          totalPrice: actualTotalPrice,
          campingTotal: campingTotalPrice,
          campingStartDate: campingDays > 0 ? effectiveCampStart : null,
          campingEndDate: campingDays > 0 ? effectiveCampEnd : null,
          bringingPets,
          phoneNumber,
          address,
          city,
          state,
          zipCode,
          licensePlate,
          cascadeAnglersClient,
          legalAcknowledgment,
          boatInspectionAcknowledged,
          siteId: fishingSiteId,
          days: calculatedDays
        });
        toast({
          title: "Added to cart",
          description: "Your fishing permit has been added to the cart."
        });
      }
      navigate('/cart');
    } catch (error) {
      console.error("Error processing fishing permit:", error);
      toast({
        title: isEditMode ? "Error updating permit" : "Error adding to cart",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleBack = () => {
    navigate('/');
  };
  const handlePermitTypeChange = (type: string) => {
    setPermitType(type);
    if (type.startsWith("annual") && permitType.startsWith("daily")) {
      setCampingDays(0);
    } else if (type.startsWith("daily") && permitType.startsWith("annual")) {
      setCampingDays(days);
    }
  };
  const handleClearForm = () => {
    setPermitType("daily-all");
    setDays(1);
    setFishingStart(new Date());
    setFishingEnd(new Date());
    setCampingDays(1);
    setBringingPets(false);
    setPhoneNumber("");
    setAddress("");
    setCity("");
    setState("");
    setZipCode("");
    setLicensePlate("");
    toast({
      title: "Form cleared",
      description: "All selections have been reset to default values."
    });
  };
  const handleOpenAcknowledgeModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setAcknowledgeModalOpen(true);
  };
  const handleOpenLegalTermsModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLegalTermsModalOpen(true);
  };
  const handleOpenSafetyModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSafetyModalOpen(true);
  };
  const handleOpenBoatInspectionModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBoatInspectionModalOpen(true);
  };
  return <div className="min-h-screen bg-tribal-light">
      <DualHeader />
      
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center mb-8">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: "url('/assets/icons/MTN_VIEW_FISHING1.JPG')"
      }} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 container max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-white mb-4 text-shadow-lg text-center md:text-5xl">
            {isEditMode ? "Edit Fishing Permit" : "Fishing Permits"}
          </h1>
          <p className="text-lg text-white max-w-3xl text-shadow-md text-center">
            {isEditMode ? "Update your fishing permit details below" : "Access pristine waters and abundant fishing at Mountain View Reservoir, Sheep Creek Reservoir, Lake Billy Shaw, and the East Fork Owyhee River"}
          </p>
        </div>
      </section>
      
      <div className="container max-w-4xl mx-auto pb-16 px-4">
        
        <div className="flex items-center mb-4">
          <Button variant="outline" size="sm" onClick={handleBack} className="mr-4 hover:bg-tribal-primary/10">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
        </div>
        
        {CLOSURES.fishing && <ClosureNotice className="mb-6" message={CLOSURE_MESSAGE_GENERAL} />}

        <Tabs defaultValue={CLOSURES.fishing ? "regulations" : "permits"} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="permits" disabled={CLOSURES.fishing}>
              <span className="flex items-center gap-2">
                {isEditMode ? "Edit Permit" : "Purchase Permits"}
                {CLOSURES.fishing && <span className="text-[10px] font-semibold uppercase text-destructive">Closed</span>}
              </span>
            </TabsTrigger>
            <TabsTrigger value="regulations" disabled={isEditMode}>Rules & Regulations</TabsTrigger>
          </TabsList>
          
          <TabsContent value="permits">
            {CLOSURES.fishing ? <ClosureNotice message={CLOSURE_MESSAGE_GENERAL} /> : <Card className="bg-white shadow-md">
              <CardHeader className="bg-tribal-primary/10 border-b border-tribal-primary/20">
                <CardTitle className="text-tribal-secondary text-xl">
                  {isEditMode ? "Update Your Fishing Permit" : "Select Your Fishing Permit"}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {isEditMode && <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 text-amber-800">
                    <p className="text-sm font-medium">
                      You are editing an existing permit. After making your changes, click the "Update Permit" button to save.
                    </p>
                  </div>}
                
                {!isEditMode && <div className="bg-tribal-primary/10 border border-tribal-primary/20 rounded-md p-4 mb-6 text-tribal-secondary">
                    <p className="text-sm font-medium">
                      All permits include access to all fisheries: Mountain View Reservoir, Sheep Creek Reservoir, Lake Billy Shaw, and the East Fork Owyhee River
                    </p>
                  </div>}

                <form onSubmit={handleSubmit}>
                  <PermitTypeSelection permitType={permitType} setPermitType={handlePermitTypeChange} days={days} />
                  
                  {permitType.startsWith("daily") && <FishingDateSelection fishingStart={fishingStart} setFishingStart={setFishingStart} fishingEnd={fishingEnd} setFishingEnd={setFishingEnd} days={days} permitType={permitType} setPermitType={handlePermitTypeChange} />}
                  
                  <CampingInfo permitType={permitType} campingDays={campingDays} setCampingDays={setCampingDays} fishingStart={fishingStart} fishingEnd={fishingEnd} campingStartDate={campingStartDate} setCampingStartDate={setCampingStartDate} campingEndDate={campingEndDate} setCampingEndDate={setCampingEndDate} />
                  
                  <CustomerInfoForm customerName={customerName} setCustomerName={setCustomerName} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber} address={address} setAddress={setAddress} city={city} setCity={setCity} state={state} setState={setState} zipCode={zipCode} setZipCode={setZipCode} licensePlate={licensePlate} setLicensePlate={setLicensePlate} cascadeAnglersClient={cascadeAnglersClient} setCascadeAnglersClient={setCascadeAnglersClient} />
                  
                  <Separator className="my-6" />
                  
                  <PermitSummary permitType={permitType} days={days} campingDays={campingDays} totalPrice={totalPrice} />
                  
                  {!isEditMode && <PermitNotes />}
                  
                  <div className="mb-6 flex items-center space-x-4">
                    <Label htmlFor="bringing-pets" className="flex items-center space-x-2">
                      <Dog className="w-6 h-6 mr-2 text-tribal-primary" />
                      Bringing Pets?
                    </Label>
                    <Switch id="bringing-pets" checked={bringingPets} onCheckedChange={setBringingPets} />
                  </div>
                  
                  {!isEditMode && <div className="mb-6 mt-8">
                      <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                        <div className="flex items-start space-x-2 mt-3">
                          <Checkbox id="legal-acknowledgment" checked={legalAcknowledgment} onCheckedChange={checked => {
                        setLegalAcknowledgment(checked as boolean);
                      }} required className="mt-1" />
                          <label htmlFor="legal-acknowledgment" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            I acknowledge and agree to the <Button variant="link" className="p-0 h-auto font-medium text-tribal-primary hover:text-tribal-primary/80 underline" onClick={handleOpenLegalTermsModal}>
                              legal terms
                            </Button> and <Button variant="link" className="p-0 h-auto font-medium text-tribal-primary hover:text-tribal-primary/80 underline" onClick={handleOpenSafetyModal}>
                              safety
                            </Button> requirements for fishing on tribal lands
                          </label>
                        </div>
                        
                        <div className="flex items-start space-x-2 mt-4">
                          <Checkbox id="boat-inspection" checked={boatInspectionAcknowledged} onCheckedChange={checked => {
                        setBoatInspectionAcknowledged(checked as boolean);
                      }} className="mt-1" />
                          <label htmlFor="boat-inspection" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            I understand that a <Button variant="link" className="p-0 h-auto font-medium text-tribal-primary hover:text-tribal-primary/80 underline" onClick={handleOpenBoatInspectionModal}>
                              mandatory boat inspection
                            </Button> is required before launching any watercraft on tribal waters
                          </label>
                        </div>
                      </div>
                    </div>}
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-between mt-6">
                    <Button type="button" variant="outline" onClick={handleClearForm} className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-100">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {isEditMode ? "Reset Changes" : "Clear Form"}
                    </Button>
                    <Button type="submit" className="w-full sm:w-auto bg-tribal-primary hover:bg-tribal-primary/90" disabled={isSubmitting}>
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {isSubmitting ? "Processing..." : isEditMode ? "Update Permit" : "Add to Cart"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>}
          </TabsContent>
          
          <TabsContent value="regulations">
            <FishingRegulations />
          </TabsContent>
        </Tabs>
      </div>
      
      <Dialog open={acknowledgeModalOpen} onOpenChange={setAcknowledgeModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-tribal-secondary flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Tribal Legal Acknowledgment Details
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              Important information about your legal responsibilities on tribal lands
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] md:max-h-[60vh] overflow-auto pr-4">
            <div className="space-y-4 my-4 px-1">
              <p className="text-sm">
                By acknowledging this agreement, you understand and accept that:
              </p>
              
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 space-y-3">
                <p className="text-sm">
                  <span className="font-semibold">Tribal Jurisdiction:</span> You are subject to the legal jurisdiction of the Shoshone-Paiute Tribal Court for any violations while on tribal lands.
                </p>
                
                <p className="text-sm">
                  <span className="font-semibold">Applicable Regulations:</span> You must adhere to all provisions of the Duck Valley Hunting and Fishing Ordinance, as well as relevant federal statutes.
                </p>
                
                <p className="text-sm">
                  <span className="font-semibold">Potential Penalties:</span> Violations may result in civil or criminal proceedings, including fines, permit revocation, or other penalties as determined by tribal authorities.
                </p>
                
                <p className="text-sm">
                  <span className="font-semibold">Consent:</span> Your purchase of a permit constitutes voluntary consent to these terms and conditions.
                </p>
              </div>
              
              <p className="text-sm text-amber-700 italic">
                This acknowledgment is a legally binding agreement between you and the Shoshone-Paiute Tribes. Please ensure you understand these terms before proceeding.
              </p>
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" className="w-full sm:w-auto bg-tribal-primary hover:bg-tribal-primary/90">
                I Understand
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={legalTermsModalOpen} onOpenChange={setLegalTermsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-tribal-secondary flex items-center gap-2">
              <FileText className="h-5 w-5 text-tribal-primary" />
              Legal Terms
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              Legal terms and conditions for fishing on Shoshone-Paiute Tribal lands
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] md:max-h-[60vh] overflow-auto pr-4">
            <div className="space-y-4 my-4 px-1">
              <div className="bg-amber-50 p-4 rounded-md border border-amber-200 space-y-3">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-medium">
                    Legal and Safety Acknowledgment
                  </p>
                </div>
                
                <p className="text-sm bg-amber-100/50 p-3 rounded border border-amber-200">
                  "BY EXECUTION OF A TRIBAL HUNTING, FISHING, TRAPPING, AND CAMPING PERMIT THE APPLICANT 
                  <span className="text-tribal-primary font-semibold"> ACKNOWLEDGES </span> 
                  AND VOLUNTARILY CONSENTS TO CIVIL OR CRIMINAL ADJUDICATION IN THE SHOSHONE-PAIUTE 
                  TRIBAL COURT, FOR ANY INFRACTIONS OF THE CURRENT DUCK VALLEY HUNTING AND FISHING 
                  ORDINANCE, AND EXISTING FEDERAL STATUTES."
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 space-y-3">
                <p className="text-sm font-semibold">
                  Duck Valley Tribal Fishing Ordinance
                </p>
                
                <p className="text-sm">
                  By purchasing a fishing permit, you agree to be bound by all applicable tribal laws and regulations, including:
                </p>
                
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>Adherence to fishing limits and seasons as posted at each fishing site</li>
                  <li>Compliance with all gear restrictions and fishing methods</li>
                  <li>Proper waste disposal and environmental protection measures</li>
                  <li>Recognition of tribal sovereignty and jurisdiction</li>
                  <li>Submission to the authority of tribal conservation officers</li>
                  <li>All permit holders must have their fishing permit and photo ID on their person at all times</li>
                </ul>
                
                <p className="text-sm mt-3">
                  Violation of these terms may result in citation, fine, confiscation of equipment, or exclusion from tribal lands. All legal matters arising from your activities on tribal lands will be adjudicated by the Shoshone-Paiute Tribal Court.
                </p>
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" className="w-full sm:w-auto bg-tribal-primary hover:bg-tribal-primary/90">
                I Understand
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={safetyModalOpen} onOpenChange={setSafetyModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-tribal-secondary flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Safety Requirements
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              Important safety information for fishing on tribal lands
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] md:max-h-[60vh] overflow-auto pr-4">
            <div className="space-y-4 my-4 px-1">
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 space-y-3">
                <p className="text-sm font-semibold">
                  Personal Safety Requirements:
                </p>
                
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  
                  <li>No swimming or wading in reservoir spillways or dam areas</li>
                  <li>No alcohol consumption while operating watercraft</li>
                  <li>Maintain safe distances from other anglers</li>
                  <li>Be aware of changing weather conditions</li>
                  <li>Carry sufficient water and sun protection</li>
                </ul>
                
                <p className="text-sm font-semibold mt-3">
                  Environmental Safety:
                </p>
                
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>Properly dispose of fishing line and tackle</li>
                  <li>Clean all watercraft to prevent invasive species</li>
                  <li>Observe fire restrictions during dry seasons</li>
                  <li>Respect wildlife and maintain safe distances</li>
                  <li>Report any hazardous conditions to tribal authorities</li>
                </ul>
                
                <p className="text-sm mt-3">
                  Emergency contact: For emergencies, call Tribal Police at (775) 757-3624 (Owyhee Police Department). Cell service is limited in many areas, so plan accordingly.
                </p>
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" className="w-full sm:w-auto bg-tribal-primary hover:bg-tribal-primary/90">
                I Understand
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={boatInspectionModalOpen} onOpenChange={setBoatInspectionModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-tribal-secondary flex items-center gap-2">
              <Sailboat className="h-5 w-5 text-blue-500" />
              Mandatory Boat Inspection
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              Important information about required watercraft inspections
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] md:max-h-[60vh] overflow-auto pr-4">
            <div className="space-y-4 my-4 px-1">
              <div className="bg-blue-50 p-4 rounded-md border border-blue-200 space-y-3">
                <p className="text-sm font-medium text-blue-800">
                  Aquatic Invasive Species Prevention Program
                </p>
                
                <p className="text-sm">
                  To protect our waters from harmful invasive species like zebra and quagga mussels, all watercraft must be inspected before launch.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 space-y-3">
                <p className="text-sm font-semibold">
                  Clean, Drain, Dry Protocol:
                </p>
                
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li><strong>CLEAN:</strong> Remove all plants, animals, and mud from your boat, trailer, and equipment</li>
                  <li><strong>DRAIN:</strong> Drain all water from your boat and equipment before leaving the water access</li>
                  <li><strong>DRY:</strong> Allow your boat and equipment to dry thoroughly before entering another body of water</li>
                </ul>
                
                <p className="text-sm font-semibold mt-3">
                  Inspection Station Information:
                </p>
                
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li><strong>Location:</strong> Tribal Conservation Office at the main entrance to the reservation</li>
                  <li><strong>Hours:</strong> 7:00 AM to 5:00 PM daily (May through October)</li>
                  <li><strong>Fee:</strong> Included in your fishing permit</li>
                </ul>
                
                <p className="text-sm mt-3">
                  Any watercraft found on tribal waters without proper inspection may result in fines and permit revocation.
                </p>
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" className="w-full sm:w-auto bg-tribal-primary hover:bg-tribal-primary/90">
                I Understand
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
};
export default FishingPermits;
